// src/i18n.ts
/**
 * App language SoT: always `en` | `pl`, pinned before first React paint.
 * Avoids race where chrome (t) and Loc (resolveValue) briefly disagree when the
 * user never clicked LanguageSwitcher and LanguageDetector resolves async.
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import translationEN from './locales/en/translation.json';
import translationPL from './locales/pl/translation.json';
import solutionsSeoEN from './locales/en/solutions-seo.json';
import solutionsSeoPL from './locales/pl/solutions-seo.json';
import { mergeTranslation } from './locales/mergeTranslation';
import { normalizeLang, type AppLang } from './utils/dataResolver';

export const I18N_STORAGE_KEY = 'i18nextLng';

const resources = {
  en: { translation: mergeTranslation(translationEN, solutionsSeoEN) },
  pl: { translation: mergeTranslation(translationPL, solutionsSeoPL) },
};

function readStoredAppLang(): AppLang | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(I18N_STORAGE_KEY);
    if (!raw) return null;
    return normalizeLang(raw);
  } catch {
    return null;
  }
}

/** Browser tag → en|pl. Unknown / missing → pl (matches API getQuestion default). */
export function detectBrowserAppLang(): AppLang {
  if (typeof navigator === 'undefined') return 'pl';
  const nav = navigator.languages?.[0] || navigator.language || '';
  if (!nav) return 'pl';
  return normalizeLang(nav);
}

/** Sync resolve used at module load — same value for all first-paint views. */
export function resolveInitialAppLang(): AppLang {
  return readStoredAppLang() || detectBrowserAppLang();
}

export function persistAppLang(lang: AppLang): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(I18N_STORAGE_KEY, lang);
  } catch {
    /* private mode / blocked storage */
  }
}

const initialLng = resolveInitialAppLang();
persistAppLang(initialLng);

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    // Pin immediately — do not leave language undefined while detector runs.
    lng: initialLng,
    // Align with API defaults (getQuestion / getRecommendation use pl when unset).
    fallbackLng: 'pl',
    supportedLngs: ['en', 'pl'],
    nonExplicitSupportedLngs: true,
    load: 'languageOnly',
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: I18N_STORAGE_KEY,
    },
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: false,
    },
  });

// Keep storage in sync if detector / changeLanguage updates language later.
i18n.on('languageChanged', (lng) => {
  persistAppLang(normalizeLang(lng));
});

export default i18n;
